import React, { useEffect, useState } from 'react';
import { Navigate, Link, useLocation } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Building, Settings, Mail, Users, FileText, Layers, Home, MessageSquare, Server, Handshake, Clock, Receipt, Shield, ScrollText, Search, Cloud, Wrench, Car } from 'lucide-react';

export const AdminLayout = ({ children }) => {
  const { isAuthenticated, logout, authLoading } = useApp();
  const location = useLocation();

  // Wait for auth check to complete before redirecting
  if (authLoading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  const menuItems = [
    { path: '/admin', icon: Home, label: 'Dashboard', exact: true },
    { path: '/admin/contracts', icon: ScrollText, label: 'Contracte', highlight: true },
    { path: '/admin/work-tracking', icon: Clock, label: 'Work In/Out' },
    { path: '/admin/invoices', icon: Receipt, label: 'Invoice' },
    { path: '/admin/backup', icon: Shield, label: 'Backup' },
    { path: '/admin/apartments', icon: Building, label: 'Apartments' },
    { path: '/admin/categories', icon: Layers, label: 'Categories' },
    { path: '/admin/about', icon: FileText, label: 'About' },
    { path: '/admin/contact-settings', icon: MessageSquare, label: 'Contact' },
    { path: '/admin/policies', icon: FileText, label: 'Policies' },
    { path: '/admin/seo', icon: Search, label: 'SEO' },
    { path: '/admin/newsletter', icon: Mail, label: 'Newsletter' },
    { path: '/admin/messages', icon: Users, label: 'Messages' },
    { path: '/admin/collaborators', icon: Handshake, label: 'Colaboratori' },
    { path: '/admin/email-settings', icon: Server, label: 'Email Server' },
    { path: '/admin/settings', icon: Settings, label: 'Settings' },
    { path: '/admin/deployment', icon: Cloud, label: 'VPS Deploy', highlight: true },
    { path: '/admin/service', icon: Wrench, label: 'Service Auto/Moto', highlight: true },
    { path: '/admin/driver-calculator', icon: Car, label: 'Conducere', highlight: true },
  ];

  const isActive = (path, exact) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen pt-24 pb-12">
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="glass p-6 sticky top-28">
              <h2 className="text-xs uppercase tracking-[0.2em] font-bold text-primary mb-6">Admin Menu</h2>
              <nav className="space-y-2">
                {menuItems.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.path, item.exact);
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      data-testid={`admin-menu-${item.label.toLowerCase()}`}
                      className={`flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                        active
                          ? 'bg-primary/10 text-primary border-l-2 border-primary'
                          : 'text-muted-foreground hover:text-foreground hover:bg-white/5'
                      }`}
                    >
                      <Icon size={18} />
                      {item.label}
                    </Link>
                  );
                })}
                <button
                  onClick={logout}
                  data-testid="admin-logout-button"
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-400 hover:bg-red-500/10 transition-colors mt-6"
                >
                  Logout
                </button>
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-4">{children}</div>
        </div>
      </div>
    </div>
  );
};